import { api } from "@/api"

import type { MaybeRef } from "@vueuse/core"
import { useQuery, useQueries } from "@tanstack/vue-query"
import type { FileExportFormat } from "@/composables/options/useFileExportFormatOptions"
import type { CardScheme } from "@/composables/options/useCardSchemeOptions"

export type TransactionReportDatapoint = DeepReadonly<{
  start: Date
  end: Date
  value: number
}>

export type TransactionReport = DeepReadonly<{
  dataset: TransactionReportDatapoint[]
  granularity: Granularity
}>

export type TransactionReportQuery = Partial<{
  start: string
  end: string
  cashierId: string
  deviceId: number
  cardScheme: CardScheme
  transactionMethod: TransactionMethod
}> & {
  sources: ("unlinked" | "donation" | "sale")[]
} // It makes use of multi-select components easier if sources if empty array rather than undefined

export const retrieveTransactionReport = async (
  query: TransactionReportQuery
): Promise<TransactionReport> => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<TransactionReport>
  >("/reports/transactions/", {
    query: query,
  })
  return {
    ...body,
    dataset: body.dataset.map((entry) => ({
      ...entry,
      start: DateFns.parseISO(entry.start),
      end: DateFns.parseISO(entry.end),
    })),
  }
}

export const useTransactionReport = (
  query?: MaybeRef<TransactionReportQuery>
) =>
  useQuery(
    [BASE_TRANSACTION_REPORT_QUERY_KEY, query] as const,
    ({ queryKey: [_name, query_] }) =>
      retrieveTransactionReport(
        query_ ? unref(query_) : { sources: [] }
      ),
    { refetchInterval: 60 * 1000 }
  )

export const useTransactionReports = (
  queries: MaybeRef<MaybeRef<TransactionReportQuery>[]>
) => {
  const queryResults = useQueries({
    queries: computed(() =>
      unref(queries).map((query) => ({
        queryKey: [
          BASE_TRANSACTION_REPORT_QUERY_KEY,
          query,
        ],
        queryFn: () =>
          retrieveTransactionReport(unref(query)),
      }))
    ),
  })

  const isLoading = computed(() =>
    queryResults.some((result) => result.isLoading)
  )

  const errors = computed(() =>
    queryResults.map((result) => result.error)
  )

  const refetch = async () => {
    if (queryResults.length === 0) {
      return []
    }
    const results = await Promise.all(
      queryResults.map((result) => {
        return result.refetch()
      })
    )
    return results
  }

  return {
    queryResults,
    isLoading,
    errors,
    refetch,
  }
}

export const exportTransactionsReport = async (
  query: TransactionReportQuery,
  format?: FileExportFormat
) => {
  const response = await api.get<Blob | string>(
    "/reports/transactions/export/",
    {
      query: { ...query, format },
    }
  )
  downloadFileFromResponse(response)
}
